import type { OpenAITokenSpend } from "./openai-usage";
import type { GranularQuotaProgress } from "./types";

export interface GranularSpendContext {
  tenantId?: string | null;
  sandboxId?: string | null;
  environmentId?: string | null;
  sessionId?: string | null;
  subjectId?: string | null;
  permissionProfileId?: string | null;
  [key: string]: string | null | undefined;
}

export interface OpenAIUsageSpendEvent extends OpenAITokenSpend {
  spendEventId?: string;
  source?: "openai";
  lineItemType?: "llm_tokens";
  operation?: string;
  requestId?: string | null;
  usageContext?: GranularSpendContext;
  rawUsage?: unknown;
}

export interface RecordOpenAIUsageSpendOptions {
  apiUrl: string;
  token: string;
  usage: OpenAIUsageSpendEvent;
  context?: GranularSpendContext | null;
  metadata?: Record<string, unknown> | null;
}

export interface RecordOpenAIUsageSpendResult {
  spendEventId: string;
  inserted: boolean;
  quota: GranularQuotaProgress | null;
}

export function toGranularHttpBase(apiUrl: string): string {
  const url = new URL(apiUrl);
  if (url.protocol === "ws:") {
    url.protocol = "http:";
  } else if (url.protocol === "wss:") {
    url.protocol = "https:";
  }

  url.pathname = url.pathname
    .replace(/\/ws\/connect$/, "")
    .replace(/\/ws$/, "");
  if (!url.pathname || url.pathname === "/") {
    url.pathname = "/granular";
  }
  url.search = "";
  url.hash = "";
  return url.toString().replace(/\/$/, "");
}

function cleanIdPart(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]+/g, "_").replace(/^_+|_+$/g, "");
}

export function buildOpenAISpendEventId(
  usage: Pick<OpenAIUsageSpendEvent, "requestId">,
  context: GranularSpendContext = {},
): string | undefined {
  const requestId = usage.requestId?.trim();
  if (!requestId) return undefined;

  const scope =
    context.sessionId ||
    context.environmentId ||
    context.subjectId ||
    context.sandboxId ||
    "global";
  return ["spend", "openai", scope, requestId].map(cleanIdPart).join("_");
}

function pricingEffectiveAtSeconds(value: string | null | undefined) {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? Math.floor(parsed / 1000) : null;
}

function compactContext(context: GranularSpendContext): GranularSpendContext {
  return Object.fromEntries(
    Object.entries(context).filter(
      ([, value]) => value != null && value !== "",
    ),
  ) as GranularSpendContext;
}

function omitTenantId(context: GranularSpendContext): GranularSpendContext {
  const scopedContext = { ...context };
  delete scopedContext.tenantId;
  return scopedContext;
}

export async function recordOpenAIUsageSpend(
  options: RecordOpenAIUsageSpendOptions,
): Promise<RecordOpenAIUsageSpendResult> {
  const usageContext = compactContext({
    ...(options.usage.usageContext || {}),
    ...(options.context || {}),
  });
  const context = omitTenantId(usageContext);
  const spendEventId =
    options.usage.spendEventId ||
    buildOpenAISpendEventId(options.usage, context);
  const metadata = {
    ...(options.metadata || {}),
    ...(options.usage.rawUsage !== undefined
      ? { openaiUsage: options.usage.rawUsage }
      : {}),
    usageContext: context,
  };

  const response = await fetch(
    `${toGranularHttpBase(options.apiUrl)}/control/spend/events`,
    {
      method: "POST",
      cache: "no-store",
      headers: {
        Authorization: `Bearer ${options.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...(spendEventId ? { spendEventId } : {}),
        sandboxId: context.sandboxId || null,
        environmentId: context.environmentId || null,
        sessionId: context.sessionId || null,
        subjectId: context.subjectId || null,
        permissionProfileId: context.permissionProfileId || null,
        source: "openai",
        lineItemType: "llm_tokens",
        provider: options.usage.provider,
        model: options.usage.model,
        operation: options.usage.operation || "chat.completions",
        requestId: options.usage.requestId || null,
        inputTokens: options.usage.inputTokens,
        outputTokens: options.usage.outputTokens,
        cachedInputTokens: options.usage.cachedInputTokens,
        reasoningTokens: options.usage.reasoningTokens,
        quantity: options.usage.totalTokens,
        quantityUnit: "tokens",
        inputPricePerMillionMicros: options.usage.inputPricePerMillionMicros,
        cachedInputPricePerMillionMicros:
          options.usage.cachedInputPricePerMillionMicros,
        outputPricePerMillionMicros: options.usage.outputPricePerMillionMicros,
        amountMicros: options.usage.amountMicros,
        currency: options.usage.currency,
        pricingSource: options.usage.pricingSource,
        pricingEffectiveAt: pricingEffectiveAtSeconds(
          options.usage.pricingEffectiveAt,
        ),
        estimated: false,
        metadata,
      }),
    },
  );

  if (!response.ok) {
    throw new Error(
      `Granular spend event failed (${response.status}): ${await response.text()}`,
    );
  }

  return response.json() as Promise<RecordOpenAIUsageSpendResult>;
}
